const DEFAULT_API_BASE = '/api/v1';
const FRONTEND_HEADER = 'X-Webstat-Frontend';
const FRONTEND_HEADER_VALUE = '1';
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS', 'TRACE']);

export type ApiFieldErrors = Record<string, string[]>;

export type ApiErrorEnvelope = {
  detail: string;
  code: string;
  errors: ApiFieldErrors;
};

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly errors: ApiFieldErrors;

  constructor(status: number, envelope: ApiErrorEnvelope) {
    super(envelope.detail);
    this.name = 'ApiError';
    this.status = status;
    this.code = envelope.code;
    this.errors = envelope.errors;
  }
}

const resolveBaseUrl = (): string => {
  const envBase = import.meta.env.VITE_API_BASE_URL as string | undefined;
  if (envBase && envBase.trim().length > 0) {
    return envBase.replace(/\/$/, '');
  }
  return DEFAULT_API_BASE;
};

const API_BASE_URL = resolveBaseUrl();

const resolvePath = (path: string): string => {
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  if (path.startsWith('/')) {
    return `${API_BASE_URL}${path}`;
  }
  return `${API_BASE_URL}/${path}`;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === 'string');

const errorMessages = (value: unknown): string[] => {
  if (typeof value === 'string') return [value];
  if (Array.isArray(value)) return value.flatMap(errorMessages);
  if (isRecord(value)) return Object.values(value).flatMap(errorMessages);
  return [];
};

const parseErrorEnvelope = (value: unknown, status: number): ApiErrorEnvelope => {
  if (!isRecord(value)) {
    return { detail: `API request failed: ${status}`, code: 'INVALID_RESPONSE', errors: {} };
  }

  const errors: ApiFieldErrors = {};
  if (isRecord(value.errors)) {
    Object.entries(value.errors).forEach(([field, messages]) => {
      const normalized = isStringArray(messages) ? messages : errorMessages(messages);
      if (normalized.length) errors[field] = normalized;
    });
  }

  return {
    detail: typeof value.detail === 'string' ? value.detail : `API request failed: ${status}`,
    code: typeof value.code === 'string' ? value.code : 'REQUEST_FAILED',
    errors,
  };
};

const csrfCookie = (): string | null => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith('csrftoken='));
  return match ? decodeURIComponent(match.slice('csrftoken='.length)) : null;
};

let csrfRequest: Promise<string> | null = null;

const ensureCsrfToken = async (): Promise<string> => {
  const existing = csrfCookie();
  if (existing) return existing;

  if (!csrfRequest) {
    csrfRequest = fetch(resolvePath('/auth/csrf/'), {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json', [FRONTEND_HEADER]: FRONTEND_HEADER_VALUE },
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new ApiError(response.status, {
            detail: 'Не удалось подготовить защищённый запрос.',
            code: 'CSRF_SETUP_FAILED',
            errors: {},
          });
        }
        const token = csrfCookie();
        if (!token) {
          throw new ApiError(0, {
            detail: 'Сервер не установил CSRF cookie.',
            code: 'CSRF_COOKIE_MISSING',
            errors: {},
          });
        }
        return token;
      })
      .finally(() => {
        csrfRequest = null;
      });
  }

  return csrfRequest;
};

const responseBody = async (response: Response): Promise<unknown> => {
  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.toLowerCase().includes('application/json')) return null;
  try {
    return await response.json();
  } catch {
    return null;
  }
};

export const apiFetch = async <T>(path: string, options: RequestInit = {}): Promise<T> => {
  const method = (options.method ?? 'GET').toUpperCase();
  const requestUrl = resolvePath(path);
  const isSameOrigin =
    typeof window === 'undefined' ||
    new URL(requestUrl, window.location.origin).origin === window.location.origin;
  const headers = new Headers(options.headers);
  headers.set('Accept', 'application/json');
  headers.set(FRONTEND_HEADER, FRONTEND_HEADER_VALUE);
  if (typeof options.body === 'string' && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (!SAFE_METHODS.has(method)) {
    if (!isSameOrigin) {
      throw new ApiError(0, {
        detail: 'Изменяющие API-запросы разрешены только на текущий origin.',
        code: 'CROSS_ORIGIN_MUTATION_BLOCKED',
        errors: {},
      });
    }
    headers.set('X-CSRFToken', await ensureCsrfToken());
  }

  const response = await fetch(requestUrl, {
    ...options,
    method,
    credentials: 'same-origin',
    headers,
  });
  const body = await responseBody(response);

  if (!response.ok) {
    throw new ApiError(response.status, parseErrorEnvelope(body, response.status));
  }
  if (response.status === 204) return undefined as T;
  if (body === null) {
    throw new ApiError(response.status, {
      detail: 'Сервер вернул ответ в неподдерживаемом формате.',
      code: 'INVALID_RESPONSE',
      errors: {},
    });
  }
  return body as T;
};

export const apiFetchRussian = async <T>(
  path: string,
  options: RequestInit = {}
): Promise<T> => {
  try {
    return await apiFetch<T>(path, options);
  } catch (error) {
    if (error instanceof ApiError && error.code === 'INVALID_RESPONSE') {
      throw new ApiError(error.status, {
        detail: `Не удалось выполнить API-запрос (HTTP ${error.status}).`,
        code: error.code,
        errors: error.errors,
      });
    }
    throw error;
  }
};

export const apiBaseUrl = API_BASE_URL;
